import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, MessageSquare, Plus, ShieldOff } from 'lucide-react';
import { commsApi } from './phase567Api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { uc } from './unifiedContactsApi';
import { formatPhone } from './shared/phone';
import InboxList from './inbox/InboxList';
import ContactThread from './inbox/ContactThread';
import SuppressionDialog from './inbox/SuppressionDialog';
import usePolling from './usePolling';
import { useLiveInbox } from './LiveInboxContext';
import { Switch } from '../components/ui/switch';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';

// Ticket 154: list refresh cadence.
const LIST_POLL_MS = 15000;
const MARK_CHUNK = 50; // server cap on contacts per /inbox/mark call

// Ticket 154: fold a fresh page 1 into the loaded rows. Response rows come
// first, in response order (same contact_id replaced). With only page 1
// loaded the response IS the list, so a row that left the filter (read on
// the Unread tab, reassigned) drops out; the open contact's row always
// stays. After Load more, the extra rows follow in their original order.
export function mergeListPage(prev, incoming, { singlePage = false, keepId = '' } = {}) {
  const fresh = new Set(incoming.map((r) => r.contact_id));
  const rest = prev.filter((r) => !fresh.has(r.contact_id) && (!singlePage || r.contact_id === keepId));
  return [...incoming, ...rest];
}

// Ticket 142: rewritten for server-side paging, one row per contact. State
// (search, filters, tab, open contact) lives in the URL so a link is
// bookmarkable/shareable and survives reload.
export default function InboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const channel = searchParams.get('channel') || '';
  const tab = searchParams.get('tab') || 'all';
  const owner = searchParams.get('owner') || '';
  const c = searchParams.get('c') || '';
  const { live, refresh: refreshLive, notify, setNotifyEnabled } = useLiveInbox();
  const notifyHint = {
    denied: "Blocked in your browser's site settings.",
    unsupported: 'Not available in this browser.',
  }[notify.permission];

  const updateParams = useCallback((patch, opts) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => { if (!v) p.delete(k); else p.set(k, v); });
      return p;
    }, opts);
  }, [setSearchParams]);

  const [items, setItems] = useState([]);
  // Latest list. Every write goes through applyItems, which updates the ref
  // first, so a change queued in the same tick (poll, then mark-read) is
  // visible before React renders it.
  const itemsRef = useRef([]);
  const applyItems = useCallback((fn) => {
    const next = fn(itemsRef.current);
    itemsRef.current = next;
    setItems(next);
  }, []);
  const [cursor, setCursor] = useState(null);
  const [total, setTotal] = useState(0);
  const [unreadContacts, setUnreadContacts] = useState(0);
  const [searchTruncated, setSearchTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Ticket 195: a failed page-1 load shows an error with Retry (not the empty
  // state); a failed Load more keeps the list and turns its button into Retry.
  const [listError, setListError] = useState(null);
  const [moreFailed, setMoreFailed] = useState(false);
  const [transports, setTransports] = useState({ sms: 'stub', email: 'stub' });
  const [newOpen, setNewOpen] = useState(false);
  const [suppressionOpen, setSuppressionOpen] = useState(false);
  // Ticket 195: rows ticked for a bulk mark, and the stars / bulk call in flight.
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [starBusyIds, setStarBusyIds] = useState(() => new Set());
  // Bumped on every request so a slow, superseded answer (filter changed, or
  // Load more overtaken by a fresh filter) never overwrites a newer one.
  const requestId = useRef(0);
  // Pages fetched since the last filter change; the poll only owns the cursor
  // while this is 1 (ticket 154).
  const pagesLoaded = useRef(1);
  const openContact = useRef(c);
  openContact.current = c;

  useEffect(() => { commsApi.config().then(setTransports).catch(() => {}); }, []);

  // Changing q/channel/tab/owner resets paging and refetches page 1.
  const loadFirstPage = useCallback(() => {
    const myReq = ++requestId.current;
    setLoading(true);
    // A superseded Load more never clears its own flag, so clear it here.
    setLoadingMore(false);
    setListError(null);
    setMoreFailed(false);
    pagesLoaded.current = 1;
    commsApi.inbox({ q: q || undefined, channel: channel || undefined, tab, owner: owner || undefined, limit: 50 })
      .then((d) => {
        if (requestId.current !== myReq) return;
        applyItems(() => d.items || []);
        setCursor(d.next_cursor || null);
        setTotal(d.total || 0);
        setUnreadContacts(d.unread_contacts || 0);
        setSearchTruncated(Boolean(d.body_search_truncated));
      })
      .catch((e) => {
        if (requestId.current !== myReq) return;
        // Nothing from the previous filters may survive: rows, counts or the badge.
        applyItems(() => []);
        setCursor(null);
        setTotal(0);
        setUnreadContacts(0);
        setSearchTruncated(false);
        setListError(errorMessage(e, 'Could not load conversations'));
      })
      .finally(() => { if (requestId.current === myReq) setLoading(false); });
  }, [q, channel, tab, owner, applyItems]);
  useEffect(() => { loadFirstPage(); }, [loadFirstPage]);
  // A selection belongs to the list it was made in.
  useEffect(() => { setCheckedIds(new Set()); }, [q, channel, tab, owner]);

  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    const myReq = ++requestId.current;
    setLoadingMore(true);
    setMoreFailed(false);
    commsApi.inbox({ q: q || undefined, channel: channel || undefined, tab, owner: owner || undefined, cursor, limit: 50 })
      .then((d) => {
        if (requestId.current !== myReq) return;
        pagesLoaded.current += 1;
        applyItems((prev) => {
          const seen = new Set(prev.map((r) => r.contact_id));
          return [...prev, ...(d.items || []).filter((r) => !seen.has(r.contact_id))];
        });
        setCursor(d.next_cursor || null);
        setTotal(d.total || 0);
        setUnreadContacts(d.unread_contacts || 0);
      })
      .catch((e) => {
        if (requestId.current !== myReq) return;
        setMoreFailed(true);
        toast({ title: 'Could not load more conversations', description: errorMessage(e) });
      })
      .finally(() => { if (requestId.current === myReq) setLoadingMore(false); });
  }, [cursor, loadingMore, q, channel, tab, owner, applyItems]);

  // Ticket 154: silent page-1 refetch with the current filters (no spinner).
  // Shares requestId with the loaders, so a filter change or Load more that
  // started meanwhile wins and this answer is dropped.
  const pollList = useCallback(async () => {
    const myReq = requestId.current;
    const d = await commsApi.inbox({ q: q || undefined, channel: channel || undefined, tab, owner: owner || undefined, limit: 50 });
    if (requestId.current !== myReq) return;
    let incoming = d.items || [];
    let unreadCount = d.unread_contacts || 0;
    // The open thread marks itself read; do not let the server's not-yet-
    // updated count flash the open row (or the tab badge) unread again.
    if (document.visibilityState !== 'hidden') {
      incoming = incoming.map((r) => {
        if (r.contact_id !== openContact.current || !(r.unread > 0)) return r;
        unreadCount = Math.max(0, unreadCount - 1);
        return { ...r, unread: 0 };
      });
    }
    applyItems((prev) => {
      const merged = mergeListPage(prev, incoming, { singlePage: pagesLoaded.current <= 1, keepId: openContact.current });
      return JSON.stringify(merged) === JSON.stringify(prev) ? prev : merged;
    });
    if (pagesLoaded.current <= 1) setCursor(d.next_cursor || null);
    setTotal(d.total || 0);
    setUnreadContacts(unreadCount);
    setSearchTruncated(Boolean(d.body_search_truncated));
  }, [q, channel, tab, owner, applyItems]);
  // Paused on a failed load: the list is empty and Retry is the way back.
  usePolling(pollList, LIST_POLL_MS, { enabled: !loading && !listError, immediate: false });

  const patchRow = useCallback((contactId, patch) => {
    applyItems((prev) => prev.map((r) => (r.contact_id === contactId ? { ...r, ...patch } : r)));
  }, [applyItems]);

  const moveToTop = useCallback((contactId) => {
    applyItems((prev) => {
      const idx = prev.findIndex((r) => r.contact_id === contactId);
      if (idx <= 0) return prev;
      const copy = prev.slice();
      const [row] = copy.splice(idx, 1);
      copy.unshift(row);
      return copy;
    });
  }, [applyItems]);

  // Called by ContactThread only once it has actually marked the contact's
  // threads read, so this always corresponds to a real transition.
  const onMarkedRead = useCallback((contactId) => {
    // pollList may already have cleared the open row and its share of the
    // count; only a row still unread here is a real transition.
    const cur = itemsRef.current.find((r) => r.contact_id === contactId);
    if (cur && cur.unread > 0) setUnreadContacts((u) => Math.max(0, u - 1));
    patchRow(contactId, { unread: 0 });
    // Ticket 154: drop the nav badge now instead of at the next 15 s poll.
    refreshLive();
  }, [patchRow, refreshLive]);

  const checkRow = useCallback((contactId, on) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(contactId); else next.delete(contactId);
      return next;
    });
  }, []);
  const checkAll = useCallback((on) => {
    setCheckedIds(on ? new Set(itemsRef.current.map((r) => r.contact_id)) : new Set());
  }, []);

  // Ticket 195: star goes to GoHighLevel first, so the star only flips once the
  // server confirms. On the Starred tab an unstarred row leaves the list now.
  const toggleStar = useCallback(async (contactId, starred) => {
    setStarBusyIds((prev) => new Set(prev).add(contactId));
    try {
      await commsApi.starContact(contactId, starred);
      if (tab === 'starred' && !starred) {
        applyItems((prev) => prev.filter((r) => r.contact_id !== contactId));
        setTotal((n) => Math.max(0, n - 1));
      } else {
        patchRow(contactId, { starred });
      }
    } catch (e) {
      toast({ title: starred ? 'Could not star the conversation' : 'Could not unstar the conversation', description: errorMessage(e) });
    } finally {
      setStarBusyIds((prev) => { const next = new Set(prev); next.delete(contactId); return next; });
    }
  }, [tab, applyItems, patchRow]);

  // Ticket 195: mark the ticked rows read / unread, synced to GoHighLevel. The
  // server answers per contact; rows it refused stay ticked and as they were.
  const bulkMark = useCallback(async (read) => {
    const ids = itemsRef.current.map((r) => r.contact_id).filter((id) => checkedIds.has(id));
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);
    try {
      // The server takes MARK_CHUNK contacts per call; a longer selection goes in turns.
      const done = [];
      const failed = [];
      for (let i = 0; i < ids.length; i += MARK_CHUNK) {
        const res = await commsApi.markInbox(ids.slice(i, i + MARK_CHUNK), read);
        done.push(...(res.done || []));
        failed.push(...(res.failed || []));
      }
      const doneSet = new Set(done);
      let delta = 0;
      applyItems((prev) => prev.map((r) => {
        if (!doneSet.has(r.contact_id)) return r;
        if (read && r.unread > 0) delta -= 1;
        if (!read && !(r.unread > 0)) delta += 1;
        return { ...r, unread: read ? 0 : Math.max(1, r.unread || 0) };
      }));
      setUnreadContacts((u) => Math.max(0, u + delta));
      setCheckedIds(new Set(failed.map((f) => f.contact_id)));
      // The open thread would mark itself read again on the next poll.
      if (!read && doneSet.has(openContact.current)) updateParams({ c: '' });
      refreshLive();
      if (failed.length) {
        toast({
          title: `${failed.length} conversation${failed.length === 1 ? '' : 's'} not updated`,
          description: failed[0].detail || 'GoHighLevel did not confirm the change.',
        });
      }
    } catch (e) {
      toast({ title: read ? 'Could not mark read' : 'Could not mark unread', description: errorMessage(e) });
    } finally {
      setBulkBusy(false);
    }
  }, [checkedIds, bulkBusy, applyItems, updateParams, refreshLive]);

  const prependRow = (row) => {
    applyItems((prev) => (prev.some((r) => r.contact_id === row.contact_id) ? prev : [row, ...prev]));
  };

  const selectedRow = items.find((r) => r.contact_id === c);

  return (
    <div className="p-4 md:p-6 flex flex-col" style={{ height: 'calc(100vh - 56px)' }} data-testid="inbox-page">
      <div className="flex items-center justify-between gap-4 mb-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <MessageSquare style={{ color: '#070B5F' }} size={26} />
          <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }} data-testid="inbox-title">Inbox</h1>
        </div>
        <div className="flex items-center gap-3 self-start">
          <button type="button" onClick={() => setSuppressionOpen(true)}
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            data-testid="inbox-suppression-open">
            <ShieldOff size={14} /> Opt-outs
          </button>
          <button onClick={() => setNewOpen(true)} className="btn-gold" data-testid="inbox-new-message">
            <Plus size={14} /> New message
          </button>
        </div>
      </div>

      {/* Ticket 154 P3: per-user opt-in for browser notifications on new texts. */}
      <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
        <Switch
          id="inbox-notify"
          checked={notify.enabled}
          disabled={notify.busy || notify.permission === 'unsupported'}
          onCheckedChange={setNotifyEnabled}
          data-testid="inbox-notify-switch"
        />
        <label htmlFor="inbox-notify" className="text-sm">Notify me about new client texts</label>
        {notifyHint && (
          <span role="status" className="text-xs text-slate-500" data-testid="inbox-notify-hint">{notifyHint}</span>
        )}
        {/* The setting is per user but permission is per browser: a second device needs its own grant. */}
        {notify.enabled && notify.permission === 'default' && (
          <button type="button" onClick={() => setNotifyEnabled(true)} disabled={notify.busy}
            className="text-xs text-[#070B5F] underline" data-testid="inbox-notify-allow">
            Allow notifications in this browser
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 grid md:grid-cols-3 gap-4">
        <div className={`flex-col h-full min-h-0 ${c ? 'hidden md:flex' : 'flex'}`}>
          <InboxList
            items={items}
            total={total}
            unreadContacts={unreadContacts}
            searchTruncated={searchTruncated}
            tab={tab}
            onTabChange={(v) => updateParams({ tab: v === 'all' ? '' : v })}
            q={q}
            onQChange={(v) => updateParams({ q: v })}
            channel={channel}
            onChannelChange={(v) => updateParams({ channel: v })}
            owner={owner}
            onOwnerChange={(v) => updateParams({ owner: v })}
            selectedId={c}
            onSelect={(contactId) => updateParams({ c: contactId })}
            loading={loading}
            error={listError}
            onRetry={loadFirstPage}
            loadingMore={loadingMore}
            moreFailed={moreFailed}
            hasMore={!!cursor}
            onLoadMore={loadMore}
            live={live}
            checkedIds={checkedIds}
            onCheck={checkRow}
            onCheckAll={checkAll}
            onBulkMark={bulkMark}
            bulkBusy={bulkBusy}
            onToggleStar={toggleStar}
            starBusyIds={starBusyIds}
          />
        </div>

        {c ? (
          <ContactThread
            key={c}
            contactId={c}
            row={selectedRow}
            transports={transports}
            live={live}
            onBack={() => updateParams({ c: '' })}
            onPatchRow={patchRow}
            onMoveToTop={moveToTop}
            onMarkedRead={onMarkedRead}
          />
        ) : (
          <div className="hidden md:flex md:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm items-center justify-center text-slate-400" data-testid="thread-placeholder">
            <div className="text-center">
              <MessageSquare size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>

      {suppressionOpen && <SuppressionDialog open={suppressionOpen} onOpenChange={setSuppressionOpen} />}
      <NewMessageDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        transports={transports}
        onStarted={(conv, contact) => {
          setNewOpen(false);
          prependRow({
            contact_id: conv.contact_id,
            display_name: contact?.display_name || null,
            phone: contact?.phones?.[0]?.value || null,
            email: contact?.emails?.[0]?.value || null,
            owner_id: contact?.owner_id || null,
            latest_at: conv.last_message_at || null,
            unread: 0,
            channels: [conv.channel],
            last_channel: conv.channel,
            last_preview: conv.last_message_preview || null,
            last_conversation_id: conv.conversation_id,
            source: conv.source || 'bfy',
          });
          updateParams({ c: conv.contact_id });
        }}
      />
    </div>
  );
}

// Ticket 141: pick a contact and a channel, then open (or reopen — one
// thread per contact and channel) its conversation. The composer then shows
// whether the contact can be messaged.
function NewMessageDialog({ open, onClose, transports, onStarted }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [contact, setContact] = useState(null);
  const [channel, setChannel] = useState('sms');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const channels = ['sms', 'email'].filter((ch) => transports?.[ch] !== 'off');
  const active = channels.includes(channel) ? channel : channels[0];

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); setContact(null); setError(''); }
  }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      uc.search(q.trim(), 8)
        .then((d) => { if (alive) setResults(d.contacts || []); })
        .catch(() => { if (alive) setResults([]); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const startConv = async () => {
    if (!contact || !active || busy) return;
    setBusy(true);
    setError('');
    try {
      const conv = await commsApi.createConversation(contact.contact_id, active);
      onStarted(conv, contact);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not start the conversation');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="new-message-dialog">
        <DialogHeader><DialogTitle>New message</DialogTitle></DialogHeader>
        {channels.length === 0 ? (
          <p className="text-sm text-amber-700">Messaging from this inbox is not connected.</p>
        ) : (
          <div className="space-y-3">
            {contact ? (
              <div className="flex items-center justify-between text-sm p-2 rounded-lg bg-slate-50" data-testid="new-message-contact">
                <span>{contact.display_name}</span>
                <button className="text-xs text-slate-500 hover:underline" onClick={() => setContact(null)}>Change</button>
              </div>
            ) : (
              <div>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts by name, email or phone"
                  autoFocus data-testid="new-message-search" />
                <ul className="mt-2 max-h-60 overflow-y-auto divide-y divide-slate-100">
                  {results.map((ct) => (
                    <li key={ct.contact_id}>
                      <button onClick={() => setContact(ct)} data-testid={`new-message-pick-${ct.contact_id}`}
                        className="w-full text-left p-2 text-sm hover:bg-slate-50">
                        <div>{ct.display_name || 'Unnamed contact'}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {[ct.emails?.[0]?.value, formatPhone(ct.phones?.[0]?.value)].filter(Boolean).join(' · ')}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-2">
              {channels.map((ch) => (
                <button key={ch} onClick={() => setChannel(ch)} data-testid={`new-message-channel-${ch}`}
                  className={`px-3 py-1.5 rounded-full text-sm border ${active === ch ? 'border-[#070B5F] bg-[#070B5F]/5' : 'border-slate-200'}`}>
                  {ch === 'sms' ? 'Text' : 'Email'}
                </button>
              ))}
            </div>
            {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
            <button onClick={startConv} disabled={!contact || busy} className="btn-gold w-full" data-testid="new-message-start">
              {busy ? <Loader2 className="animate-spin" size={14} /> : null} Open conversation
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
